import { Intersection, Matrix3, Quaternion, Vector3 } from "three";
import { Engine, Entity } from "../../engine/Engine";
import { History } from "../../history/History";
import {
  POINTER_GESTURE_MOVE,
  POINTER_GESTURE_PINCH_BEGIN,
  POINTER_GESTURE_PRESS,
  POINTER_GESTURE_RELEASE,
  type PointerGestureMoveDetail,
  type PointerGesturePressDetail,
  type PointerGestureReleaseDetail,
} from "../../input/PointerGestureInterpreter";
import { SugarModel } from "../../loader/SugarModel";
import { SugarModelConfigurator } from "../../loader/SugarModelConfigurator";
import {
  getModelObject,
  getNearestSelectableSugarModel,
  pickIntersectionForSceneSelection,
  raycast,
  resolveSugarModelClickTarget,
} from "../../physics/PhysicsUtil";
import { Line } from "../../shape/Line";
import { Room } from "../../shape/Scene";
import {
  clampWallMountedY,
  isWallOnlyPlacement,
  pickFloorGrabSurface,
  pickGrabSurface,
  pickVerticalGrabSurface,
  setupDragPlane,
  setupFloorDragPlane,
  setupVerticalDragPlane,
  type GrabRay,
} from "../../util/SurfaceMovement";
import { getLocalBoundingBox } from "../../util/GeometryUtil";
import {
  clampBeamBottomY,
  isStructuralElevatableKind,
  isStructuralFloorFreeKind,
  isStructuralModelData,
  placeTypeOfModelData,
  sizeFromStructuralData,
  type BeamMoveMode,
} from "../../shape/structural/StructuralCatalog";
import {
  DragConstraintPipeline,
  ProductSceneMover,
} from "./index";
import { WallCollisionConstraint } from "./constraints/WallCollisionConstraint";
import { OBJECT_COLLISION_HITS } from "./collision/ObstacleCollision";
import { JointSnapConstraint } from "./constraints/JointSnapConstraint";
import type { PlanarGraphController } from "../../shape/segment/PlanarGraphController";
import { PLANAR_GRAPH_CHANGED_EVENT } from "../../shape/segment/segmentPointerEvents";
import { projectPointOnPath } from "../../shape/segment/axis/pathHelpers";
import { isSelectToolState } from "../scene/DesignerToolMode";
import { PlanarGraphWallCollisionProvider } from "./collision/PlanarGraphWallCollisionProvider";
import {
  productWallAnchorFromPoint,
  productWallAnchorsTransform,
  type ProductSurfaceAnchor,
} from "../scene/SurfaceAnchor";
/**
 * Sürüklenen ürünün yüzey anchor'larını okuyup yazan taraf.
 * Room designer `RoomAnchorAdapter` ile karşılar (anchor grafiğini de günceller);
 * configurator hiç vermez, o zaman anchor mantığı çalışmaz.
 */
export interface ProductAnchorStore {
  productAnchorsOf(model: SugarModel): ProductSurfaceAnchor[];
  setProductAnchors(
    model: SugarModel,
    anchors: readonly ProductSurfaceAnchor[],
  ): void;
}
import type { DesignerElementEventChannels } from "../events/ElementEventPlatform";
import { DesignerChannels } from "../events/DesignerChannels";
import {
  DESIGNER_CLEAR_SELECTION_COMMAND,
  DESIGNER_DRAG_ENDED_EVENT,
  DESIGNER_DRAG_STARTED_EVENT,
  DESIGNER_DRAWER_STATE_EVENT,
  DESIGNER_PRODUCT_TRANSFORMED_EVENT,
  DESIGNER_REQUEST_RENDER_COMMAND,
  DESIGNER_SELECTABLE_OFF_COMMAND,
  DESIGNER_SELECTABLE_ON_COMMAND,
  DESIGNER_SELECT_PRODUCT_COMMAND,
  DESIGNER_SELECT_WALL_COMMAND,
  type DesignerSelectProductDetail,
  type DesignerSelectWallDetail,
} from "../events/DesignerEventContracts";
import { ProductMoveSession } from "./ProductMoveSession";

/** Geçici: yüzey hareketi dışında constraint pipeline kapalı (sınıflar silinmedi). */
const SURFACE_CONSTRAINTS_ENABLED = true;

/** CFG (and other hosts) inject product grab/drop side-effects without a parallel transformer. */
export type DesignerTransformerLifecycleHooks = {
  beforeGrab?: (model: SugarModel) => void;
  beforeCommitDrop?: (
    model: SugarModel,
  ) => boolean | Promise<boolean>;
  afterDrop?: (model: SugarModel) => void | Promise<void>;
  setOrbitEnabled?: (enabled: boolean) => void;
};

/**
 * Oda tasarımcısı model sürükleme + seçim.
 * Girdi yalnızca {@link PointerGestureInterpreter} `events` hedefi üzerinden
 * (`pointer-gesture-*`); canvas DOM dinleyicisi yok.
 *
 * Constraint uygulaması {@link ProductSceneMover} + {@link DragConstraintPipeline}
 * üzerinden (CFG ile aynı çekirdek). Wall/joint constraint set’i RD’ye özel.
 * Pointer lifecycle {@link ProductMoveSession} / SelectInteraction’ta kalır.
 */
export class DesignerTransformer extends Entity {
  private _scene: Room;

  private _selectedModel?: SugarModel;
  private _selectedWall: Line | undefined;
  private _selectedModelPlane = {
    position: new Vector3(0, 0, 0),
    normal: new Vector3(0, 1, 0),
  };
  private _selectedPosition = new Vector3(0, 0, 0);
  private _selectedModelDragging = false;
  private _activeProductMove?: ProductMoveSession;
  private _grabPosePosition = new Vector3();
  private _grabPoseQuaternion = new Quaternion();
  private _grabLocal = new Vector3();
  private _grabPoseAnchors: ProductSurfaceAnchor[] = [];
  private _pendingSurfaceAnchors: ProductSurfaceAnchor[] = [];
  private _anchorPreviewCancelled = false;

  private _draggingHoldActive = false;
  private _pointerGestureLock = false;

  private _pointerSource: EventTarget | null = null;
  private _detachPointer?: () => void;

  private _pipeline: DragConstraintPipeline;
  private _mover: ProductSceneMover;
  private _wallConstraint: WallCollisionConstraint;
  private _jointSnapConstraint: JointSnapConstraint;
  private _selectToolActive = true;
  private _detachDrawerState?: () => void;
  private _lifecycle?: DesignerTransformerLifecycleHooks;
  private _selfGestures = false;
  /** Press started a product session but detach/grab waits for drag threshold. */
  private _productGrabArmed = false;
  private _pressClientX = 0;
  private _pressClientY = 0;
  private _pendingHitPoint = new Vector3();
  private _pendingGrabRay?: GrabRay;
  private static readonly _DRAG_THRESHOLD_PX = 2;
  /** Beams only: horizontal = XZ floor drag; vertical = Y elevation. */
  private _beamMoveMode: BeamMoveMode = "horizontal";

  private _emitDraggingStart(): void {
    if (this._draggingHoldActive) return;
    this._draggingHoldActive = true;
    this._channels.channels.transform.events.emit(
      DESIGNER_DRAG_STARTED_EVENT,
      undefined,
    );
  }

  private _emitDraggingEnd(): void {
    if (!this._draggingHoldActive) return;
    this._draggingHoldActive = false;
    this._channels.channels.transform.events.emit(
      DESIGNER_DRAG_ENDED_EVENT,
      undefined,
    );
  }

  private _selectProduct(detail: DesignerSelectProductDetail): void {
    this._channels.channels.selection.commands.emit(
      DESIGNER_SELECT_PRODUCT_COMMAND,
      detail,
    );
  }

  private _selectWall(detail: DesignerSelectWallDetail): void {
    this._channels.channels.selection.commands.emit(
      DESIGNER_SELECT_WALL_COMMAND,
      detail,
    );
  }

  private _clearSelection(): void {
    this._channels.channels.selection.commands.emit(
      DESIGNER_CLEAR_SELECTION_COMMAND,
      undefined,
    );
  }

  private _setSelectable(selectable: boolean): void {
    const command = selectable
      ? DESIGNER_SELECTABLE_ON_COMMAND
      : DESIGNER_SELECTABLE_OFF_COMMAND;
    this._channels.channels.selection.commands.emit(command, undefined);
  }

  private _emitProductTransformed(model: SugarModel): void {
    this._channels.channels.transform.events.emit(
      DESIGNER_PRODUCT_TRANSFORMED_EVENT,
      { model },
    );
  }

  private _requestRender(): void {
    this._channels.channels.transform.commands.emit(
      DESIGNER_REQUEST_RENDER_COMMAND,
      undefined,
    );
  }

  private _releasePointerGestureLock(): void {
    this._pointerGestureLock = false;
  }

  /** Yalnızca çoklu dokunuş (pinch); tek parmak pan/pitch model sürüklemeyi kesmez. */
  abortModelDragForCameraGesture(): void {
    if (
      !this._selectedModelDragging &&
      !this._pointerGestureLock &&
      !this._productGrabArmed
    ) {
      return;
    }
    this._releasePointerGestureLock();
    this._productGrabArmed = false;
    if (this._selectedModelDragging || this._mover.active) {
      this.restoreGrabPoseToModel();
      if (this._mover.active) this._mover.cancel();
      this._selectedModelDragging = false;
      this._clearObjectCollisionHitsEvent();
      this._emitDraggingEnd();
    }
    // Always restore orbit — even if grab never crossed the drag threshold.
    this._lifecycle?.setOrbitEnabled?.(true);
  }

  private _isModelDragActivatorGesture(d: PointerGesturePressDetail): boolean {
    if (d.pointerType === "mouse") {
      if (d.button !== 0) return false;
      if (d.modifiers.ctrl || d.modifiers.meta || d.modifiers.shift)
        return false;
      return true;
    }
    if (d.pointerType === "touch") {
      return d.isPrimary;
    }
    return true;
  }

  private _domEventForUiDetail(
    event: PointerGesturePressDetail | PointerGestureReleaseDetail,
  ): Event {
    if (event.sourceEvent) return event.sourceEvent;
    return event as unknown as Event;
  }

  private _grabRay(
    event?: DragEvent | MouseEvent | PointerGesturePressDetail,
  ): GrabRay | undefined {
    if (!event || !this.engine) return undefined;
    return {
      event: event as GrabRay["event"],
      camera: this.engine.activeCamera!.instance,
      dom: this.engine.canvas,
    };
  }

  get beamMoveMode(): BeamMoveMode {
    return this._beamMoveMode;
  }

  setBeamMoveMode(mode: BeamMoveMode): void {
    this._beamMoveMode = mode === "vertical" ? "vertical" : "horizontal";
  }

  private _isElevatable(model: SugarModel): boolean {
    return isStructuralElevatableKind(
      (model.data as { structuralKind?: string } | undefined)?.structuralKind,
    );
  }

  private _usesVerticalMove(model: SugarModel): boolean {
    return this._isElevatable(model) && this._beamMoveMode === "vertical";
  }

  /** Grab pose + drag plane. */
  private _initDragGrab(
    model: SugarModel,
    anchor: Vector3,
    ray?: GrabRay,
  ): void {
    if (model instanceof SugarModelConfigurator) {
      // World pose — config parent may not be identity until/after attach.
      model.updateMatrixWorld(true);
      model.getWorldPosition(this._grabPosePosition);
      model.getWorldQuaternion(this._grabPoseQuaternion);
      setupFloorDragPlane(
        this._selectedModelPlane,
        this._grabPosePosition,
        ray,
      );
      this._selectedPosition.copy(this._grabPosePosition);
      return;
    }

    this._grabPosePosition.copy(model.position);
    this._grabPoseQuaternion.copy(model.quaternion);
    if (this._usesVerticalMove(model)) {
      setupVerticalDragPlane(
        this._selectedModelPlane,
        model.position,
        ray,
      );
    } else {
      setupDragPlane(
        this._selectedModelPlane,
        this._scene,
        model,
        anchor,
        ray,
        this._segments,
      );
    }
    this._selectedPosition.copy(model.position);
  }

  beginProductMove(
    d: PointerGesturePressDetail,
    target: SugarModel,
    pickedModel: SugarModel,
    hit: Intersection,
    intersections: Intersection[],
  ): ProductMoveSession | null {
    if (!this.enabled || !this._selectToolActive) return null;
    if (this._pointerGestureLock) return null;
    if (!this._isModelDragActivatorGesture(d)) return null;
    this._grabLocal.copy(target.worldToLocal(hit.point.clone()));

    const freshlyPicked = this._selectedModel !== target;
    const uiEvent = this._domEventForUiDetail(d);
    if (freshlyPicked) {
      this._selectProduct({
        model: target,
        sourceModel: pickedModel !== target ? pickedModel : undefined,
        part: undefined,
        intersections: intersections ?? [],
        event: uiEvent,
      });
      this._selectedModel = target;
    }

    this._selectedModel = target;
    this._pointerGestureLock = true;
    // Defer detach/grab until the pointer actually moves — click / double-click
    // must not yank a piece out of its configurator (position → 0 bugs).
    this._productGrabArmed = true;
    this._selectedModelDragging = false;
    this._pressClientX = d.clientX;
    this._pressClientY = d.clientY;
    this._pendingHitPoint.copy(hit.point);
    this._pendingGrabRay = this._grabRay(d);
    this._activeProductMove = new ProductMoveSession(this);
    return this._activeProductMove;
  }

  clearActiveProductMoveSession(session: ProductMoveSession): void {
    if (this._activeProductMove === session) {
      this._activeProductMove = undefined;
    }
  }

  private _activateProductGrab(): void {
    const target = this._selectedModel;
    if (!target || this._mover.active || !this._productGrabArmed) return;
    this._productGrabArmed = false;
    if (!(target instanceof SugarModelConfigurator)) {
      this._beginAnchorPreview(target);
    }
    this._selectedModelDragging = true;
    this._emitDraggingStart();
    this._lifecycle?.setOrbitEnabled?.(false);
    this._lifecycle?.beforeGrab?.(target);

    if (SURFACE_CONSTRAINTS_ENABLED) {
      this._mover.grab(target);
    }

    this._initDragGrab(target, this._pendingHitPoint, this._pendingGrabRay);
  }

  updateProductMove(d: PointerGestureMoveDetail): void {
    if (this._productGrabArmed && !this._selectedModelDragging) {
      const dist = Math.hypot(
        d.clientX - this._pressClientX,
        d.clientY - this._pressClientY,
      );
      if (dist <= DesignerTransformer._DRAG_THRESHOLD_PX) return;
      this._activateProductGrab();
    }
    if (this._selectedModelDragging) this._move(d as any);
  }

  commitProductMove(): void {
    this._releasePointerGestureLock();
    const model = this._selectedModel;
    const didGrab = this._selectedModelDragging || this._mover.active;
    this._productGrabArmed = false;
    try {
      if (model && didGrab) {
        this._finishDrag(model, true);
        this._selectedModelDragging = false;
        this._clearObjectCollisionHitsEvent();
        this._emitDraggingEnd();
      } else {
        this._selectedModelDragging = false;
        this._lifecycle?.setOrbitEnabled?.(true);
      }
    } catch (err) {
      this._selectedModelDragging = false;
      this._clearObjectCollisionHitsEvent();
      this._emitDraggingEnd();
      this._lifecycle?.setOrbitEnabled?.(true);
      console.error("commitProductMove failed", err);
    } finally {
      this._activeProductMove = undefined;
    }
  }

  cancelProductMove(): void {
    this.abortModelDragForCameraGesture();
    this._activeProductMove = undefined;
  }

  applyProductSelection(
    event: PointerGestureReleaseDetail,
    target?: SugarModel,
    sourceModel?: SugarModel,
    intersections: Intersection[] = [],
  ): void {
    if (!target) {
      this._clearSelection();
      this._selectedModel = undefined;
      this._selectedWall = undefined;
      return;
    }
    this._selectProduct({
      model: target,
      sourceModel: sourceModel !== target ? sourceModel : undefined,
      part: undefined,
      intersections,
      event: this._domEventForUiDetail(event),
    });
    this._selectedModel = target;
  }

  setProductHover(selectable: boolean): void {
    if (selectable && !this._onselectable) {
      this._setSelectable(true);
      this._onselectable = true;
    } else if (!selectable && this._onselectable) {
      this._setSelectable(false);
      this._onselectable = false;
    }
  }

  /** Always present — falls back to a private platform (see DesignerChannels). */
  private readonly _channels: DesignerChannels;

  constructor(
    scene: Room,
    pointerGestures?: EventTarget | null,
    pipeline?: DragConstraintPipeline,
    private readonly _segments?: PlanarGraphController,
    private readonly _anchors?: ProductAnchorStore,
    _events?: DesignerElementEventChannels,
  ) {
    super();
    this._channels = new DesignerChannels(_events);
    this._scene = scene;
    this._pointerSource = pointerGestures ?? null;
    this._wallConstraint = new WallCollisionConstraint(
      scene,
      _segments
        ? new PlanarGraphWallCollisionProvider(_segments)
        : undefined,
    );
    this._jointSnapConstraint = new JointSnapConstraint(scene);
    this._pipeline =
      pipeline ??
      (SURFACE_CONSTRAINTS_ENABLED
        ? new DragConstraintPipeline()
            .add(this._wallConstraint)
            .add(this._jointSnapConstraint)
        : new DragConstraintPipeline());
    this._mover = new ProductSceneMover(this._pipeline);
  }

  /** Pipeline'a ek constraint eklemek için dışarıdan erişim. */
  get pipeline(): DragConstraintPipeline {
    return this._pipeline;
  }

  /**
   * Intact configurator root joint-snap / config↔config merge.
   * Default true (Room Designer). CFG sets false on bind.
   */
  set allowConfiguratorRootSnap(value: boolean) {
    this._jointSnapConstraint.allowConfiguratorRootSnap = value;
  }

  get allowConfiguratorRootSnap() {
    return this._jointSnapConstraint.allowConfiguratorRootSnap;
  }

  /** Joint snap capture radius (world units). Default 0.2 (RD). */
  set jointSnapDistance(value: number) {
    this._jointSnapConstraint.snapDistance = value;
  }

  get jointSnapDistance() {
    return this._jointSnapConstraint.snapDistance;
  }

  /** Shared grab/move/drop core (CFG ile aynı sınıf). */
  get mover(): ProductSceneMover {
    return this._mover;
  }

  get scene(): Room {
    return this._scene;
  }



  /** CFG / no-router: attach gesture listeners on the transformer itself. */
  enableSelfGestures(): void {
    this._selfGestures = true;
    if (this.engine) this._attachPointerGestures();
  }

  setLifecycleHooks(hooks: DesignerTransformerLifecycleHooks | undefined): void {
    this._lifecycle = hooks;
  }

  begin(): void {
    // Select-mode pointer ownership belongs to DesignerInteractionRouter.
    // CFG (no router) uses enableSelfGestures().
  }

  override onAdd(engine: Engine): void {
    super.onAdd(engine);
    if (this._selfGestures) this._attachPointerGestures();
    this._channels.bindEngine(engine);
    this._detachDrawerState = this._channels.channels.configuration.events.on(
      DESIGNER_DRAWER_STATE_EVENT,
      this._onDrawerState,
    );
    engine.addEventListener(PLANAR_GRAPH_CHANGED_EVENT, this._onGraphChanged);
  }

  override onRemove(engine: Engine): void {
    this._detachDrawerState?.();
    this._detachDrawerState = undefined;
    engine.removeEventListener(
      DESIGNER_DRAWER_STATE_EVENT,
      this._onDrawerState,
    );
    engine.removeEventListener(PLANAR_GRAPH_CHANGED_EVENT, this._onGraphChanged);
    this._detachPointerGestures();
  }

  animate(): void {}

  get control() {
    return undefined;
  }

  private _onGestureAbort = (): void => {
    this.abortModelDragForCameraGesture();
  };

  private _onGesturePress = (ev: Event): void => {
    const d = (ev as CustomEvent<PointerGesturePressDetail>).detail;
    if (!d) return;
    if (
      d.pointerType === "touch" &&
      !d.isPrimary &&
      (this._selectedModelDragging || this._pointerGestureLock)
    ) {
      this.abortModelDragForCameraGesture();
      return;
    }
    if (d.activePointerCount > 1) {
      this.abortModelDragForCameraGesture();
      return;
    }
    const intersections = raycast(d as any, this._scene.models);
    const hit = pickIntersectionForSceneSelection(intersections);
    if (!hit) {
      this._clearSelection();
      this._selectedModel = undefined;
      this._selectedWall = undefined;
      return;
    }
    const picked = getNearestSelectableSugarModel(hit.object);
    if (!picked) {
      this._clearSelection();
      this._selectedModel = undefined;
      this._selectedWall = undefined;
      return;
    }
    const target =
      resolveSugarModelClickTarget(hit.object, {
        selected: this._selectedModel,
      }) ?? picked;
    const session = this.beginProductMove(d, target, picked, hit, intersections);
    if (!session) return;
    d.sourceEvent?.stopImmediatePropagation();
  };

  private _onGestureMove = (ev: Event): void => {
    const d = (ev as CustomEvent<PointerGestureMoveDetail>).detail;
    if (!d) return;
    if (this._activeProductMove && (this._selectedModelDragging || this._productGrabArmed)) {
      if (d.activePointerCount !== 1) {
        this._activeProductMove.cancel();
        return;
      }
      this._activeProductMove.move(d);
    } else if (this._selectedModel && this._selectedModelDragging) {
      if (d.activePointerCount !== 1) {
        this.abortModelDragForCameraGesture();
        return;
      }
      this._move(d as any);
    } else if (this.enabled) {
      if (d.activePointerCount === 1) {
        this._over(d as any);
      }
    }
  };

  private _onGestureRelease = (ev: Event): void => {
    const d = (ev as CustomEvent<PointerGestureReleaseDetail>).detail;
    if (!d) return;
    const isPrimaryRelease =
      d.pointerType !== "mouse" || d.button === 0;
    if (!isPrimaryRelease) return;

    if (this._activeProductMove && (this._selectedModelDragging || this._productGrabArmed)) {
      this._activeProductMove.commit(d);
      this._applySelectionFromRaycast(d);
      return;
    }

    const wasDraggingModel = this._selectedModelDragging;
    const draggedModel = this._selectedModel;
    this._releasePointerGestureLock();
    if (this._selectedModelDragging) {
      if (draggedModel) {
        this._finishDrag(draggedModel, true);
      }
      this._selectedModelDragging = false;
      this._clearObjectCollisionHitsEvent();
    }
    if (wasDraggingModel) {
      this._emitDraggingEnd();
    }

    this._applySelectionFromRaycast(d);
  };

  private _attachPointerGestures(): void {
    this._detachPointerGestures();
    const t = this._pointerSource;
    if (!t) return;
    t.addEventListener(POINTER_GESTURE_PRESS, this._onGesturePress);
    t.addEventListener(POINTER_GESTURE_MOVE, this._onGestureMove);
    t.addEventListener(POINTER_GESTURE_RELEASE, this._onGestureRelease);
    t.addEventListener(POINTER_GESTURE_PINCH_BEGIN, this._onGestureAbort);
    this._detachPointer = () => {
      t.removeEventListener(POINTER_GESTURE_PRESS, this._onGesturePress);
      t.removeEventListener(POINTER_GESTURE_MOVE, this._onGestureMove);
      t.removeEventListener(POINTER_GESTURE_RELEASE, this._onGestureRelease);
      t.removeEventListener(POINTER_GESTURE_PINCH_BEGIN, this._onGestureAbort);
      this._detachPointer = undefined;
    };
  }

  private _detachPointerGestures(): void {
    this._detachPointer?.();
    this._detachPointer = undefined;
  }

  grabModel(sugarModel: SugarModel, event?: DragEvent | MouseEvent) {
    this._selectedModel = sugarModel;
    this._beginAnchorPreview(sugarModel);
    if (SURFACE_CONSTRAINTS_ENABLED) {
      this._mover.grab(sugarModel);
    }
    this._initDragGrab(sugarModel, sugarModel.position, this._grabRay(event));
    this._selectedModelDragging = true;
    (sugarModel as any).grabbed = true;
    this._emitDraggingStart();
  }

  /**
   * Async load() sonrası mesh/OBB değişirse collision working set'i yeniler.
   * Grab poz/düzlem referansına dokunmaz.
   */
  refreshCollisionWorkingAfterModelMeshUpdate(model: SugarModel): void {
    if (!SURFACE_CONSTRAINTS_ENABLED) return;
    if (this._selectedModel !== model) return;
    if (!(model as { grabbed?: boolean }).grabbed) return;
    this._wallConstraint.refresh(model);
  }

  moveModel(_sugarModel: SugarModel, event?: DragEvent | MouseEvent) {
    if (event) this._move(event as any);
  }

  get selectedModel(): SugarModel | undefined {
    return this._selectedModel;
  }

  clearSelection(): void {
    if (this._selectedModelDragging) this.abortModelDragForCameraGesture();
    this._selectedModel = undefined;
    this._selectedWall = undefined;
    this._beamMoveMode = "horizontal";
    this._clearSelection();
  }

  private _clearObjectCollisionHitsEvent(): void {
    this.engine?.dispatchCustomEvent(OBJECT_COLLISION_HITS, {
      detail: { hits: [], scene: this.engine?.activeScene },
    });
  }

  dropModel() {
    const m = this._selectedModel;
    if (m) {
      this._finishDrag(m, true);
      (m as any).grabbed = false;
    }
    this._clearObjectCollisionHitsEvent();
    this._selectedModelDragging = false;
    this._emitDraggingEnd();
    this._releasePointerGestureLock();
    this._selectedModel = undefined;
  }

  /** Grab anındaki pozisyon/rotasyona geri döner (iptal/başa dön için). */
  restoreGrabPoseToModel(): void {
    const m = this._selectedModel;
    if (!m) return;
    m.position.copy(this._grabPosePosition);
    m.quaternion.copy(this._grabPoseQuaternion);
    m.updateMatrixWorld(true);
    if (this._grabPoseAnchors.length > 0) {
      this._anchors?.setProductAnchors(m, this._grabPoseAnchors);
    } else {
      this._anchors?.setProductAnchors(m, []);
    }
    this._pendingSurfaceAnchors = [];
    this._anchorPreviewCancelled = true;
  }

  private _finishDrag(model: SugarModel, commitHistory: boolean): void {
    const runFinish = async (): Promise<void> => {
      if (this._lifecycle?.beforeCommitDrop) {
        const ok = await this._lifecycle.beforeCommitDrop(model);
        if (!ok) {
          if (SURFACE_CONSTRAINTS_ENABLED && this._mover.active) {
            this._mover.cancel();
          }
          this._lifecycle.setOrbitEnabled?.(true);
          return;
        }
      }
      if (SURFACE_CONSTRAINTS_ENABLED && this._mover.active) {
        this._mover.drop();
      }
      this._pendingSurfaceAnchors = this._pendingSurfaceAnchors.map((previous) => {
        if (previous.kind !== "wall" || !this._segments) return previous;
        const spec = this._segments.getSegment(previous.segmentId);
        if (!spec) return previous;
        const projection = projectPointOnPath(spec.path, {
          x: model.position.x,
          z: model.position.z,
        });
        const offset = Math.max(
          0,
          (model.position.x - projection.point.x) *
            projection.normal.x *
            previous.side +
            (model.position.z - projection.point.z) *
              projection.normal.z *
              previous.side,
        );
        return productWallAnchorFromPoint(
          spec,
          { x: model.position.x, z: model.position.z },
          model.position.y,
          previous.side,
          offset,
        );
      });
      if (this._anchorPreviewCancelled) {
        // restoreGrabPoseToModel kalıcı anchor'ı zaten geri yükledi.
      } else if (this._pendingSurfaceAnchors.length > 0) {
        this._anchors?.setProductAnchors(model, this._pendingSurfaceAnchors);
      } else {
        this._anchors?.setProductAnchors(model, []);
      }
      this._grabPoseAnchors = [];
      this._pendingSurfaceAnchors = [];
      this._anchorPreviewCancelled = false;
      if (commitHistory) {
        History.of(this.engine!)?.snapshot("Move product");
      }
      await this._lifecycle?.afterDrop?.(model);
      this._lifecycle?.setOrbitEnabled?.(true);
    };
    void runFinish();
  }

  /** Beam dikey mod: XZ sabit, world Y dikey düzlem kesişiminden. */
  private _moveBeamVertical(
    model: SugarModel,
    event: MouseEvent | TouchEvent | DragEvent | PointerGestureMoveDetail,
  ): void {
    const ray = this._grabRay(event as any);
    if (!ray) return;

    const surface = pickVerticalGrabSurface(this._grabPosePosition, ray);
    const deltaY = surface.point.y - this._selectedModelPlane.position.y;
    const kind = isStructuralModelData(model.data)
      ? model.data.structuralKind
      : undefined;
    const height = isStructuralModelData(model.data)
      ? sizeFromStructuralData(model.data).height
      : 0.3;
    const y = clampBeamBottomY(
      this._selectedPosition.y + deltaY,
      height,
      kind,
    );
    const candidate = new Vector3(
      this._grabPosePosition.x,
      y,
      this._grabPosePosition.z,
    );

    let finalPosition = candidate;
    if (SURFACE_CONSTRAINTS_ENABLED) {
      finalPosition = this._mover.resolve(candidate);
      // Vertical mode must not slide on XZ from wall push-out.
      finalPosition.x = this._grabPosePosition.x;
      finalPosition.z = this._grabPosePosition.z;
      finalPosition.y = clampBeamBottomY(finalPosition.y, height, kind);
    }

    if (
      !Number.isFinite(finalPosition.x) ||
      !Number.isFinite(finalPosition.y) ||
      !Number.isFinite(finalPosition.z)
    ) {
      console.warn("beam vertical finalPosition is not finite", finalPosition);
      this.abortModelDragForCameraGesture();
      return;
    }

    model.position.copy(finalPosition);
    model.quaternion.copy(this._grabPoseQuaternion);
    model.updateMatrixWorld(true);
    this._pendingSurfaceAnchors = [
      {
        kind: "floor",
        x: model.position.x,
        z: model.position.z,
        y: model.position.y,
      },
    ];
    this._clearObjectCollisionHitsEvent();
    this._emitProductTransformed(model);
  }

  private _move(event: MouseEvent | TouchEvent | DragEvent | PointerGestureMoveDetail) {
    const m = this._selectedModel;
    if (!m || !this.engine) return;

    const ray = this._grabRay(event as any);
    if (!ray) return;

    if (this._usesVerticalMove(m)) {
      this._moveBeamVertical(m, event);
      return;
    }

    const surface =
      m instanceof SugarModelConfigurator
        ? pickFloorGrabSurface(this._selectedPosition, ray)
        : pickGrabSurface(
            this._scene,
            m,
            m.position,
            ray,
            this._segments,
          );

    this._wallConstraint.setIgnoredSupport(surface.supportModel);

    if (this._selectedModelPlane.normal.dot(surface.normal) <= 0.99) {
      if (surface.rotation) {
        m.quaternion.copy(surface.rotation);
        this._grabPoseQuaternion.copy(surface.rotation);
      }
      if (surface.segmentId) {
        const grabOffset = this._grabLocal
          .clone()
          .applyQuaternion(m.quaternion);
        m.position.copy(surface.point).sub(grabOffset);
        m.updateMatrixWorld(true);
        let minDistance = Number.POSITIVE_INFINITY;
        for (const obb of m.getCollisionWorldObbs()) {
          const localNormal = surface.normal
            .clone()
            .applyMatrix3(new Matrix3().copy(obb.rotation).transpose());
          const support =
            Math.abs(localNormal.x) * obb.halfSize.x +
            Math.abs(localNormal.y) * obb.halfSize.y +
            Math.abs(localNormal.z) * obb.halfSize.z;
          const centerDistance = obb.center
            .clone()
            .sub(surface.point)
            .dot(surface.normal);
          minDistance = Math.min(minDistance, centerDistance - support);
        }
        if (Number.isFinite(minDistance) && minDistance < 0) {
          m.position.addScaledVector(surface.normal, -minDistance + 1e-4);
        }
        m.updateMatrixWorld(true);
        this._wallConstraint.refresh(m);
      } else {
        m.position.copy(surface.point);
      }
      m.updateMatrixWorld(true);
      this._selectedModelPlane.normal.copy(surface.normal);
      this._selectedModelPlane.position.copy(surface.point);
      this._selectedPosition.copy(m.position);
    }

    const diffPosition = this._selectedPosition
      .clone()
      .add(surface.point.clone().sub(this._selectedModelPlane.position));

    if (
      !Number.isFinite(diffPosition.x) ||
      !Number.isFinite(diffPosition.y) ||
      !Number.isFinite(diffPosition.z)
    ) {
      console.warn("diffPosition is not finite", diffPosition);
      this.abortModelDragForCameraGesture();
      return;
    }

    const placeType = placeTypeOfModelData(m.data);
    const objectSurface =
      placeType === "FLOORANDOBJECT" || placeType === "WALLANDFLOORANDOBJECT";
    if (!this._isElevatable(m) && !surface.segmentId && objectSurface) {
      diffPosition.y = surface.point.y;
    }

    if (SURFACE_CONSTRAINTS_ENABLED) {
      let finalPosition = this._mover.resolve(diffPosition);
      const structuralKind = (m.data as { structuralKind?: string } | undefined)
        ?.structuralKind;
      const onFloorSurface =
        !surface.segmentId && surface.normal.y > 0.5;
      // Floor-free structural: no wall re-align. Dual-surface items on the
      // floor also skip wall lock so they can leave the wall.
      const wallSnap =
        !isStructuralFloorFreeKind(structuralKind) &&
        !(
          placeTypeOfModelData(m.data) === "FLOORANDWALL" &&
          onFloorSurface
        );
      const segmentContact = wallSnap
        ? this._wallConstraint.lastSegmentWallContact
        : undefined;
      if (segmentContact) {
        m.position.copy(finalPosition);
        m.updateMatrixWorld(true);
        this._wallConstraint.refresh(m);
        this._wallConstraint.lockAlignmentForNextApply(segmentContact);
        finalPosition = this._mover.resolve(finalPosition);
      }
      if (
        !Number.isFinite(finalPosition.x) ||
        !Number.isFinite(finalPosition.y) ||
        !Number.isFinite(finalPosition.z)
      ) {
        console.warn("finalPosition is not finite", finalPosition);
        // Abort rather than leave orbit/transformer disabled mid-drag.
        this.abortModelDragForCameraGesture();
        return;
      }
      // Elevatable items: keep world Y from grab (floor plane drag is XZ-only).
      if (this._isElevatable(m)) {
        finalPosition.y = this._selectedPosition.y;
      } else if (!surface.segmentId && objectSurface) {
        finalPosition.y = surface.point.y;
        if (!surface.supportModel) m.quaternion.copy(this._grabPoseQuaternion);
      } else if (isWallOnlyPlacement(placeType)) {
        const box = getLocalBoundingBox(m);
        const size = new Vector3();
        box?.getSize(size);
        finalPosition.y = clampWallMountedY(finalPosition.y, size.y);
      }
      m.position.copy(finalPosition);
      this._updateSurfaceAnchor(m, surface, segmentContact);
      this.engine?.dispatchCustomEvent(OBJECT_COLLISION_HITS, {
        detail: {
          hits: this._wallConstraint.lastHits,
          separations: this._wallConstraint.lastSeparations,
          appliedPush: this._wallConstraint.lastAppliedPush,
          appliedPushes: this._wallConstraint.lastAppliedPushes,
          cornerSnaps: this._wallConstraint.lastCornerSnaps,
          scene: this.engine?.activeScene,
        },
      });
      this._emitProductTransformed(m);
      return;
    }

    if (this._isElevatable(m)) {
      diffPosition.y = this._selectedPosition.y;
    } else if (!surface.segmentId && objectSurface) {
      diffPosition.y = surface.point.y;
      if (!surface.supportModel) m.quaternion.copy(this._grabPoseQuaternion);
    } else if (isWallOnlyPlacement(placeType)) {
      const box = getLocalBoundingBox(m);
      const size = new Vector3();
      box?.getSize(size);
      diffPosition.y = clampWallMountedY(diffPosition.y, size.y);
    }
    m.position.copy(diffPosition);
    this._updateSurfaceAnchor(m, surface);
    m.quaternion.copy(this._grabPoseQuaternion);
    m.updateMatrixWorld(true);
    this._clearObjectCollisionHitsEvent();
    this._emitProductTransformed(m);
  }

  private _onselectable = false;
  private _over(
    event: MouseEvent | DragEvent | TouchEvent | PointerGestureMoveDetail,
  ) {
    const intersections = raycast(
      event as any,
      this._scene.raycastingTargets,
      this.engine!.activeCamera?.instance,
      this.engine?.canvas,
    );
    const pick = pickIntersectionForSceneSelection(intersections);

    if (pick) {
      this._setSelectable(true);
      this._onselectable = true;
    } else if (this._onselectable) {
      this._setSelectable(false);
      this._onselectable = false;
    }
  }

  private _applySelectionFromRaycast(
    eventLike: PointerGestureReleaseDetail,
  ): void {
    const intersections = raycast(
      eventLike as any,
      this._scene.raycastingTargets,
    );
    const hit = pickIntersectionForSceneSelection(intersections);
    const uiEvent = this._domEventForUiDetail(eventLike);

    if (!hit) {
      this._clearSelection();
      this._selectedModel = undefined;
      this._selectedWall = undefined;
    } else {
      const pickedModel = getModelObject(hit.object);
      if (pickedModel) {
        if (pickedModel instanceof SugarModel) {
          const sourceModel =
            getNearestSelectableSugarModel(hit.object) ?? pickedModel;
          const target =
            resolveSugarModelClickTarget(hit.object, {
              doubleClick: eventLike.isDoubleClick,
              selected: this._selectedModel,
            }) ?? sourceModel;
          this._selectProduct({
            model: target,
            sourceModel: sourceModel !== target ? sourceModel : undefined,
            part: undefined,
            intersections: intersections ?? [],
            event: uiEvent,
          });
          this._selectedModel = target;
        } else {
          this._selectWall({
            wall: pickedModel,
            intersections: intersections ?? [],
            event: uiEvent,
            ref: (pickedModel as any).userData?.parent,
          });
          this._selectedWall = pickedModel as Line;
        }
      }
    }
  }

  private _onDrawerState = (event: Event): void => {
    this._selectToolActive = isSelectToolState(
      (event as CustomEvent<string>).detail ?? "",
    );
    if (!this._selectToolActive) this.abortModelDragForCameraGesture();
  };

  private _updateSurfaceAnchor(
    model: SugarModel,
    surface: ReturnType<typeof pickGrabSurface>,
    contactOverride?: { segmentId: string; normal: Vector3 },
  ): void {
    const floorAnchor: ProductSurfaceAnchor = {
      kind: "floor",
      x: model.position.x,
      z: model.position.z,
      y: model.position.y,
    };
    const structuralKind = isStructuralModelData(model.data)
      ? model.data.structuralKind
      : undefined;
    if (isStructuralFloorFreeKind(structuralKind)) {
      this._pendingSurfaceAnchors = [floorAnchor];
      return;
    }
    const contacts = [
      ...(contactOverride ? [contactOverride] : []),
      ...this._wallConstraint.lastSegmentWallContacts,
    ];
    if (surface.segmentId) {
      contacts.push({
        segmentId: surface.segmentId,
        normal: surface.normal.clone(),
      });
    }
    const uniqueContacts = contacts.filter(
      (contact, index) =>
        contacts.findIndex(
          (candidate) => candidate.segmentId === contact.segmentId,
        ) === index,
    );
    const anchors: ProductSurfaceAnchor[] = [];
    if (this._segments) {
      for (const contact of uniqueContacts.slice(0, 2)) {
        const spec = this._segments.getSegment(contact.segmentId);
        if (!spec) continue;
        const projection = projectPointOnPath(spec.path, {
          x: model.position.x,
          z: model.position.z,
        });
        const side: 1 | -1 =
          projection.normal.x * contact.normal.x +
              projection.normal.z * contact.normal.z >=
            0
            ? 1
            : -1;
        const normalOffset = Math.max(
          0,
          (model.position.x - projection.point.x) *
            projection.normal.x *
            side +
            (model.position.z - projection.point.z) *
              projection.normal.z *
              side,
        );
        anchors.push(
          productWallAnchorFromPoint(
          spec,
          { x: model.position.x, z: model.position.z },
          model.position.y,
          side,
          normalOffset,
          ),
        );
      }
    }
    this._pendingSurfaceAnchors = anchors.length > 0 ? anchors : [floorAnchor];
  }

  private _beginAnchorPreview(model: SugarModel): void {
    const anchors = this._anchors?.productAnchorsOf(model) ?? [];
    this._grabPoseAnchors = anchors.map((anchor) => ({ ...anchor }));
    this._pendingSurfaceAnchors = [];
    this._anchorPreviewCancelled = false;
    this._anchors?.setProductAnchors(model, []);
  }

  private _onGraphChanged = (): void => {
    if (!this._segments) return;
    for (const child of this._scene.models ?? []) {
      if (!(child instanceof SugarModel)) continue;
      const anchors = this._anchors?.productAnchorsOf(child) ?? [];
      const wallAnchors = anchors.filter(
        (anchor): anchor is Extract<ProductSurfaceAnchor, { kind: "wall" }> =>
          anchor.kind === "wall",
      );
      if (wallAnchors.length === 0) continue;
      const validAnchors = wallAnchors.filter((anchor) =>
        Boolean(this._segments!.getSegment(anchor.segmentId)),
      );
      if (validAnchors.length === 0) {
        const floorAnchor = {
          kind: "floor",
          x: child.position.x,
          z: child.position.z,
          y: child.position.y,
        } satisfies ProductSurfaceAnchor;
        this._anchors?.setProductAnchors(child, [floorAnchor]);
        if (child === this._selectedModel && this._selectedModelDragging) {
          this._selectedPosition.copy(child.position);
          this._selectedModelPlane.position.copy(child.position);
          this._selectedModelPlane.normal.set(0, 1, 0);
          this._wallConstraint.refresh(child);
        }
        continue;
      }
      const specs = validAnchors.map((anchor) =>
        this._segments!.getSegment(anchor.segmentId),
      );
      const transform = productWallAnchorsTransform(
        specs.filter((spec): spec is NonNullable<typeof spec> => Boolean(spec)),
        validAnchors,
      );
      if (!transform) continue;
      this._anchors?.setProductAnchors(child, validAnchors);
      child.position.copy(transform.position);
      child.quaternion.copy(transform.quaternion);
      child.updateMatrixWorld(true);
      if (child === this._selectedModel && this._selectedModelDragging) {
        const wallNormal = new Vector3(0, 0, 1)
          .applyQuaternion(transform.quaternion)
          .normalize();
        this._selectedPosition.copy(child.position);
        this._selectedModelPlane.position.copy(child.position);
        this._selectedModelPlane.normal.copy(wallNormal);
        this._wallConstraint.refresh(child);
      }
    }
    this._requestRender();
  };
}

